import get from 'lodash.get';
import i18next from "i18next";
import {locales} from "Constants/constants";
import SRS from '../../../../services/StudentRecordService';
import getPercentileColor from "../../util/getPercentileColor";
import {fetchTranslatedTips} from "./fetchTips";
import {getParentActivitiesForStudents} from "../../../../services/API";
import {fetchARMScore, getARMAssessment} from "../../util/getArmScore";
import {getScaledBenchmarkCutlines} from "../../util/getScaledBenchmarkCutlines";
import getSeason from "../../util/getSeason";
import {getPR} from "../../functions/getPR";
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../../services/util';
import { getScreeningWindowDetails } from '../../../../services/screeningWindowService';
import getLatestARMBox from '../../util/getLatestARMBox';
import { isPendingAssessment } from '../../util/pendingAssessment';
import { generateSubscores } from '../../util/generators/generateSubscores';
import { isBenchmarkAssessment } from '../../util/getActivityType';
import getWindowFromSeason from '../../util/getWindowFromSeason';

/**
 * The main function for fetching the parent report data
 * @param {*} student
 * @param {*} metric 
 * @param {*} benchmark 
 * @param {*} customBenchmarks 
 * @param {*} equating 
 * @param {*} locale 
 * @returns 
 */
export async function fetchParentReport(student, metric, benchmark, customBenchmarks, reportConfig, equating, locale) {
  const t = i18next.getFixedT(null, 'teacher_parent_report');
  const studentId = student && student.value;
  
  /**
   * fetch all the data we need for the report
   */
  let assessments = await getLatestAssessments(studentId, equating, locale, 30);
  assessments = assessments.filter(assessment => !isPendingAssessment(assessment)); // filter out pending assessments
  assessments = assessments.filter(isBenchmarkAssessment); // filter to only benchmark assessments

  /**
   * generate report components
  */
  let subscores = null;
  if (reportConfig.isSubscoresVisible) {
    subscores = await generateSubscores(studentId, locale);
  }

  /**
   * calculate the other report data
   */
  const armBoxes = await getARMBoxes(student, assessments, metric, benchmark, customBenchmarks, locale);
  const latestARMBox = getLatestARMBox(armBoxes);
  const {armScore, score} = latestARMBox;
  const isSpanishEarlyReader = assessments?.[0]?.displayStatus === 'prereader' && locale === 'ES-MX';

  let tips = null;
  if (reportConfig.isTipsVisible) {
    tips = await fetchTranslatedTips(armScore, locale);
  }

  const reportNotAvailable = isSpanishEarlyReader || isNullOrUndefined(score);

  /**
   * return the final report object
   */
  return {
    reportNotAvailable,
    armBoxes,
    subscores,
    tips,
  };
}

/**
 * helpers and generators
 */
async function getLatestAssessments(studentId, equating, locale, limit) {
  const statuses = ["not_started","scored"];
  const displayStatuses = ["completed","rescored","prereader"];
  const assessments = await getParentActivitiesForStudents([{id: studentId}], limit, ["assessment"], equating, locale, statuses, displayStatuses);
  return assessments;
}

async function getLatestScores(studentId, assessments) {
  const areaScore = get(assessments, "[0].scores.areaScore");
  let armScore = assessments && assessments.length ? await fetchARMScore(studentId, assessments) : null;
  const wcpm = get(assessments, "[0].scores.wcpmScore");
  const createdAt = get(assessments, "[0].createdAt")
  const armAssessment = await getARMAssessment(studentId, assessments);
  if (isNotNullOrUndefined(armScore)) {
    if (parseInt(armScore) === armScore) {
      // armScore does not have any decimals
      armScore = parseFloat(armScore).toFixed(1);
    } else armScore = parseFloat(armScore).toFixed(2);
  }
  
  return {
    areaScore: areaScore && parseFloat(areaScore).toFixed(2),
    armScore,
    wcpm: wcpm && Math.round(wcpm),
    createdAt: createdAt,
    armAssessment, // assessment used to determine arm score
    wcpmAssessment: assessments?.[0], // assessment used to determine wcpm
  }
}

function getPercentileRank(score, metric, benchmark, customBenchmarks, locale, date) {
  const scale = metric.value === 'ORF' ? 'WCPM' : 'ARM';
  const season = getSeason(date);
  const window = getWindowFromSeason(season);
  const {cutoffs} = getScaledBenchmarkCutlines(benchmark, scale, season, customBenchmarks, locale);

  let pacing = "AT_GRADE_LEVEL";
  if (score >= cutoffs.GOOD) pacing = "AHEAD_OF_GRADE_LEVEL";
  if (score < cutoffs.POOR) pacing = "BELOW_GRADE_LEVEL";

  const percentile = getPR({
    score,
    scale: scale,
    grade: benchmark.grade,
    language: locale,
    window: window,
  });

  return {
    percentile,
    pacing,
  }
}

async function getARMBoxes(student, assessments, metric, benchmark, customBenchmarks, locale) {
  const t = i18next.getFixedT(null, 'teacher_parent_report');
  const studentId = student && student.value;
  const armBoxes = {};
  const windows = ['BOY', 'MOY', 'EOY'];
  for (const window of windows) {
    const {startDate, endDate} = getScreeningWindowDetails(window);
    const assessmentsInWindow = assessments.filter(assessment => {
      const activityDate = new Date(assessment.createdAt);
      return activityDate > startDate && endDate > activityDate;
    });
    const latestScores = await getLatestScores(studentId, assessmentsInWindow);
    const armScore = locale !== locales.ES_MX ? latestScores.armScore : latestScores.areaScore;
    const score = metric.value === 'ORF' ? latestScores.wcpm : armScore;
    const scoreDate = latestScores.createdAt;
    const {percentile, pacing} = getPercentileRank(score, metric, benchmark, customBenchmarks, locale, scoreDate);
    armBoxes[window] = {
      armScore,
      score,
      label: metric.value === 'ORF' ? 'WCPM' : 'ARM',
      metric: metric.value,
      percentile,
      color: getPercentileColor(pacing),
      pacing,
      window,
      benchmarkGrade: benchmark.grade,
      assessment: (metric.value === 'ARM' && locale === locales.EN_US) ? latestScores.armAssessment : latestScores.wcpmAssessment,
    }
  }
  return armBoxes;
}